import { ReactElement } from 'react'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedMessage, useIntl } from 'react-intl'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { useMutation } from '@apollo/client'
import styled from 'styled-components'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { Dropdown } from 'components/v2/Dropdown'
import { DotsIcon } from 'components/Icons'
import List from 'components/v2/List'
import { StyledLink } from 'components/v2/Link'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canDisablePassengerProperty, canEnablePassengerProperty, canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { DISABLE_PASSENGER_PROPERTY, ENABLE_PASSENGER_PROPERTY } from 'features/PassengerProperties/mutations-legacy'
import { ActivateAction } from 'components/ActivateAction'
import { IDateRangeItemProps, IPassengerPropertiesList, IPassengerPropertiesStatusProps } from './types'
import { FormattedPercent } from '@10xLabs/react-web/components/FormattedPercent'
import { GTPassengerPropertyPassengerType } from 'gql/types.generated'
import IconButton from 'components/v2/IconButton'

const PassengerPropertyStatus = styled.span<IPassengerPropertiesStatusProps>`
  font-weight: 500;
  color: ${({ isEnabled }): string => (isEnabled ? '#12d4aa' : 'unset')};
`
const DateContainer = styled.div`
  display: flex;
  white-space: nowrap;
`
const DateRangeItem = ({ dateRanges }: IDateRangeItemProps): ReactElement => {
  const intl = useIntl()
  return (
    <div>
      {dateRanges.length > 0 ? (
        <Dropdown toggle={<StyledLink>{dateRanges.length}</StyledLink>} noPadding fitContent>
          <Table>
            <Thead>
              <Tr>
                <Th>
                  <FormattedMessage id="dictionary.startsOn" defaultMessage="Starts on" />
                </Th>
                <Th>
                  <FormattedMessage id="dictionary.endsOn" defaultMessage="Ends on" />
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {dateRanges.map((range, index) => {
                return (
                  <Tr key={index}>
                    <Td>
                      <DateContainer>
                        <FormattedDate intl={intl} value={range.startAt} month="2-digit" day="2-digit" year="numeric" />{' '}
                        <FormattedTimeByDate date={range.startAt} intl={intl} />
                      </DateContainer>
                    </Td>
                    <Td>
                      <DateContainer>
                        <FormattedDate value={range.endAt} month="2-digit" day="2-digit" year="numeric" />{' '}
                        <FormattedTimeByDate date={range.endAt} intl={intl} />
                      </DateContainer>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </Dropdown>
      ) : (
        '-'
      )}
    </div>
  )
}

export const PassengerTypesColumnItem = ({
  passengerTypes
}: {
  passengerTypes: GTPassengerPropertyPassengerType[]
}): ReactElement => {
  return (
    <div>
      {passengerTypes.length > 0 ? (
        <Dropdown toggle={<StyledLink>{passengerTypes.length}</StyledLink>} noPadding fitContent>
          <Table>
            <Tbody>
              {passengerTypes.map((passengerType, index) => {
                return (
                  <Tr key={index}>
                    <Td>{passengerType.name}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </Dropdown>
      ) : (
        '-'
      )}
    </div>
  )
}
export const PassengerPropertiesList = ({ passengerProperties }: IPassengerPropertiesList): ReactElement => {
  const login = useLogin()
  const [enablePassengerProperty, { loading: enableLoading, error: enableError }] = useMutation(
    ENABLE_PASSENGER_PROPERTY,
    {
      refetchQueries: ['GetPassengerProperties']
    }
  )
  const [disablePassengerProperty, { loading: disableLoading, error: disableError }] = useMutation(
    DISABLE_PASSENGER_PROPERTY,
    {
      refetchQueries: ['GetPassengerProperties']
    }
  )

  const toggleActivation = (passengerPropertyId: string, isActive: boolean): void => {
    if (!isActive) {
      enablePassengerProperty({
        variables: {
          input: {
            id: passengerPropertyId
          }
        }
      })
    } else {
      disablePassengerProperty({
        variables: {
          input: {
            id: passengerPropertyId
          }
        }
      })
    }
  }
  const canEdit = canWrite(FEATURES.PASSENGER_PROPERTIES, login.accessToken.policies)
  const canToogleActivation =
    canEnablePassengerProperty(FEATURES.PASSENGER_PROPERTIES, login.accessToken.policies) ||
    canDisablePassengerProperty(FEATURES.PASSENGER_PROPERTIES, login.accessToken.policies)
  const canSeeActions = canEdit || canToogleActivation
  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {canSeeActions && <Th />}
            <Th>
              <FormattedMessage id="dictionary.status" defaultMessage="Status" />
            </Th>
            <Th>
              <FormattedMessage id="passengerProperties.list.column.propertyName" defaultMessage="Property Name" />
            </Th>
            <Th>
              <FormattedMessage id="passengerProperties.list.column.passengerType" defaultMessage="Passenger Type" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.country" defaultMessage="Country" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.salesChannel" defaultMessage="Sales Channel" />
            </Th>
            <Th>
              <FormattedMessage id="dictionary.discount" defaultMessage="Discount" />
            </Th>
            <Th>
              <FormattedMessage
                id="passengerProperties.list.column.name.departureDates"
                defaultMessage="Departure Dates"
              />
            </Th>
            <Th>
              <FormattedMessage id="passengerProperties.list.column.name.searchDates" defaultMessage="Search Dates" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {passengerProperties.map((property) => {
            return (
              <Tr key={property.id}>
                {canSeeActions && (
                  <Td>
                    <Dropdown
                      toggle={<IconButton icon={<DotsIcon />} noBorder smallPadding />}
                      noPadding
                      noIndicator
                      left
                    >
                      <List.Container>
                        {canEdit && (
                          <List.Item noPadding>
                            <List.Link to={`/passenger-properties/${property.id}`}>
                              <FormattedMessage
                                id="passengers.properties.list.actions.edit"
                                defaultMessage="Edit passenger property"
                              />
                            </List.Link>
                          </List.Item>
                        )}
                        {canToogleActivation && (
                          <List.Item
                            action
                            onClick={(): void => toggleActivation(property.id, property.enabled)}
                            loading={enableLoading || disableLoading}
                          >
                            <ActivateAction isActive={property.enabled} error={enableError || disableError} />
                          </List.Item>
                        )}
                      </List.Container>
                    </Dropdown>
                  </Td>
                )}
                <Td>
                  <PassengerPropertyStatus isEnabled={property.enabled}>
                    {property.enabled ? (
                      <FormattedMessage id="dictionary.active" defaultMessage="Active" />
                    ) : (
                      <FormattedMessage id="dictionary.inactive" defaultMessage="Inactive" />
                    )}
                  </PassengerPropertyStatus>
                </Td>
                <Td>{property.name}</Td>
                <Td>
                  <PassengerTypesColumnItem
                    passengerTypes={property.settings[0].passengerTypes as GTPassengerPropertyPassengerType[]}
                  />
                </Td>
                <Td>{property.countryCode}</Td>
                <Td>{property.settings[0]?.salesChannelsV2.map(({ name }) => name).join(', ')}</Td>
                <Td>
                  <FormattedPercent value={property.settings[0]?.discount} />
                </Td>
                <Td>
                  <DateRangeItem dateRanges={property.departureDateRanges} />
                </Td>
                <Td>
                  <DateRangeItem dateRanges={property.searchDateRanges} />
                </Td>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
